<div class="page-container">
  <div class="flex flex-col gap-6">
    <app-page-header
      title="Integrations"
      description="Connect tools to sync bookings, meetings, and CRM data with your forms." />

    <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex flex-wrap items-center gap-2">
        <label class="integrations-filter">
          <select
            class="integrations-filter-select"
            aria-label="Filter by connection"
            [value]="appFilter()"
            (change)="onAppFilterChange($event)">
            <option value="all">All Apps</option>
            <option value="connected">Connected</option>
            <option value="available">Available</option>
          </select>
        </label>

        <label class="integrations-filter">
          <select
            class="integrations-filter-select"
            aria-label="Filter by category"
            [value]="categoryFilter()"
            (change)="onCategoryFilterChange($event)">
            <option value="all">All Categories</option>
            <option value="scheduling">Scheduling</option>
            <option value="meetings">Meetings</option>
            <option value="crm">CRM</option>
          </select>
        </label>
      </div>

      <div class="flex items-center gap-1 self-end sm:self-auto" role="group" aria-label="View mode">
        <button
          type="button"
          class="integrations-view-btn"
          [class.integrations-view-btn--active]="viewMode() === 'grid'"
          aria-label="Grid view"
          [attr.aria-pressed]="viewMode() === 'grid'"
          (click)="setViewMode('grid')">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 4h7v7H4V4Zm9 0h7v7h-7V4ZM4 13h7v7H4v-7Zm9 0h7v7h-7v-7Z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          class="integrations-view-btn"
          [class.integrations-view-btn--active]="viewMode() === 'list'"
          aria-label="List view"
          [attr.aria-pressed]="viewMode() === 'list'"
          (click)="setViewMode('list')">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 7h16M4 12h16M4 17h16"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    @if (filteredIntegrations().length === 0) {
      <div class="rounded-2xl border border-dashed border-neutral-200 bg-white px-6 py-12 text-center">
        <p class="text-sm font-medium text-neutral-800">No integrations match these filters</p>
        <p class="mt-1 text-sm text-neutral-500">Try a different app or category filter.</p>
      </div>
    } @else if (viewMode() === 'grid') {
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        @for (item of filteredIntegrations(); track item.id) {
          <article class="integrations-card flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
            <div class="flex items-start justify-between gap-3">
              <span
                class="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-neutral-100 text-neutral-700">
                @if (item.iconSrc) {
                  <img
                    [src]="item.iconSrc"
                    [alt]="item.label"
                    class="h-7 w-7 object-contain" />
                } @else {
                  <ng-container
                    *ngTemplateOutlet="categoryIcon; context: { $implicit: item.category }" />
                }
              </span>
              <button
                type="button"
                class="integrations-status-btn"
                [class.integrations-status-btn--connected]="isConnected(item.id)"
                (click)="toggleConnection(item)">
                {{ isConnected(item.id) ? 'Connected' : 'Connect' }}
              </button>
            </div>
            <div class="flex flex-col gap-1.5">
              <h3 class="text-sm font-semibold text-neutral-950">{{ item.label }}</h3>
              <p class="text-xs leading-5 text-neutral-500">{{ item.description }}</p>
            </div>
          </article>
        }
      </div>
    } @else {
      <ul class="flex flex-col gap-3">
        @for (item of filteredIntegrations(); track item.id) {
          <li
            class="integrations-card flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div class="flex min-w-0 items-start gap-3 sm:items-center">
              <span
                class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-neutral-100 text-neutral-700">
                @if (item.iconSrc) {
                  <img
                    [src]="item.iconSrc"
                    [alt]="item.label"
                    class="h-7 w-7 object-contain" />
                } @else {
                  <ng-container
                    *ngTemplateOutlet="categoryIcon; context: { $implicit: item.category }" />
                }
              </span>
              <div class="min-w-0">
                <h3 class="text-sm font-semibold text-neutral-950">{{ item.label }}</h3>
                <p class="mt-1 text-xs leading-5 text-neutral-500">{{ item.description }}</p>
              </div>
            </div>
            <button
              type="button"
              class="integrations-status-btn self-end sm:self-auto"
              [class.integrations-status-btn--connected]="isConnected(item.id)"
              (click)="toggleConnection(item)">
              {{ isConnected(item.id) ? 'Connected' : 'Connect' }}
            </button>
          </li>
        }
      </ul>
    }
  </div>
</div>

<ng-template #categoryIcon let-category>
  <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    @switch (category) {
      @case ('scheduling') {
        <path
          d="M7 4v2M17 4v2M5 9h14M6 6h12a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round" />
      }
      @case ('meetings') {
        <path
          d="M4 8h11v9H4V8Zm11 2.5 5-2.5v9l-5-2.5"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linejoin="round" />
      }
      @default {
        <path
          d="M8 12h8M12 8v8M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round" />
      }
    }
  </svg>
</ng-template>
